import { useQuery } from "@tanstack/react-query";
import CategoriesService from "../../services/CategoriesService";

// API response types (snake_case from API)
interface ApiSubCategory {
  id: number;
  title: string;
}

interface ApiCategory {
  id: number;
  title: string;
  helptext: string;
  launch_only: boolean;
  category_url: string;
  image_url: string;
  average_salary: number;
  sub_categories: ApiSubCategory[];
}

interface ApiCategoriesResponse {
  status: boolean;
  data: ApiCategory[];
}

// Client-side types (camelCase for our application)
interface SubCategory {
  id: number;
  title: string;
}

interface Category {
  id: number;
  title: string;
  helpText: string;
  launchOnly: boolean;
  categoryUrl: string;
  imageUrl: string;
  averageSalary: number;
  subCategories: SubCategory[];
}

// Mapper functions to transform API response to client-side format
const mapSubCategory = (apiSubCategory: ApiSubCategory): SubCategory => ({
  id: apiSubCategory.id,
  title: apiSubCategory.title
});

const mapCategory = (apiCategory: ApiCategory): Category => ({
  id: apiCategory.id,
  title: apiCategory.title,
  helpText: apiCategory.helptext,
  launchOnly: apiCategory.launch_only,
  categoryUrl: apiCategory.category_url,
  imageUrl: apiCategory.image_url,
  averageSalary: apiCategory.average_salary,
  subCategories: apiCategory.sub_categories.map(mapSubCategory)
});

const mapCategoriesData = (apiData: ApiCategory[]): Category[] =>
  apiData.map(mapCategory);

// Query key factory for categories
export const categoriesKeys = {
  all: ["categories"] as const,
  list: () => [...categoriesKeys.all, "list"] as const
};

/**
 * React Query hook to fetch categories
 * Uses React Query's built-in caching mechanism with select function for data transformation
 *
 * @param options - React Query options
 * @returns Query result with categories data
 */
export const useCategoriesQuery = <TData = Category[]>(
  options: {
    enabled?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    select?: (data: ApiCategory[]) => TData;
  } = {}
) => {
  const { select, ...queryOptions } = options;

  return useQuery({
    queryKey: categoriesKeys.list(),
    queryFn: async (): Promise<ApiCategory[]> => {
      try {
        const response = await CategoriesService.getCategories();

        // Handle potential undefined response
        if (!response || !response.data) {
          throw new Error("No data received from categories API");
        }

        const { data } = response.data;

        // Return raw API data - transformation will happen in select function
        return data;
      } catch (error) {
        console.error("Error fetching categories:", error);
        throw error;
      }
    },
    // Transform API response from snake_case to camelCase using select function
    select: select || (mapCategoriesData as (data: ApiCategory[]) => TData),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours - categories don't change frequently
    refetchOnWindowFocus: false,
    retry: false,
    ...queryOptions
  });
};

/**
 * Hook to get categories data with loading and error states
 * Provides a simplified interface similar to Redux selectors
 */
export const useCategories = () => {
  const { data, isLoading, error, isError } = useCategoriesQuery();

  return {
    categories: data || [],
    isLoading,
    error: isError ? error : null,
    hasData: !!data?.length
  };
};

// Export types for use in other components
export type {
  // Client-side types (camelCase)
  Category,
  SubCategory,
  // API types (snake_case) - exported for reference but prefer using client-side types
  ApiCategory,
  ApiSubCategory,
  ApiCategoriesResponse
};
